import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import "./styles/VideoClipper.css";

import { Button } from "./Button";
import { formatTime } from "../common/helpers";
import { FileService } from "../services/FileService";
import { useJobs } from "../providers/JobsProvider";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../common/app-routes";
import toast from "react-hot-toast";

// ----- time helpers -----

// "HH:MM:SS.mmm" -> ms
const parseTime = (str: string) => {
  if (!str) return 0;
  const [h, m, rest] = str.split(":");
  if (rest === undefined) return 0;
  const [s, ms = "0"] = rest.split(".");
  const hours = parseInt(h, 10) || 0;
  const mins = parseInt(m, 10) || 0;
  const secs = parseInt(s, 10) || 0;
  const millis = parseInt(ms, 10) || 0;
  return hours * 3600000 + mins * 60000 + secs * 1000 + millis;
};

interface VideoClipperProps {
  /** How many processing minutes the user has already used this month */
  usedProcessingMinutes?: number;
  /** Max processing minutes allowed this month */
  maxProcessingMinutes?: number;

  changeActiveSection: (id: string) => void;
}

const VideoClipper: React.FC<VideoClipperProps> = ({
  usedProcessingMinutes,
  maxProcessingMinutes,
}) => {
  const navigate = useNavigate();
  const { watchJob } = useJobs();

  // mode: upload or url
  const [mode, setMode] = useState<"upload" | "url">("upload");

  // data source
  const [file, setFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>("");

  // output format
  const [outputFormat, setOutputFormat] = useState<number>(0);

  // NEW: output file name
  const [outputFileName, setOutputFileName] = useState<string>("");

  // timing defaults
  const [startMs, setStartMs] = useState<number>(0);
  const [endMs, setEndMs] = useState<number>(31150);

  // what is currently in the inputs as text
  const [startInput, setStartInput] = useState<string>(formatTime(0));
  const [endInput, setEndInput] = useState<string>(formatTime(31150));

  // so we don't stomp the user while they're typing
  const [isEditingStart, setIsEditingStart] = useState(false);
  const [isEditingEnd, setIsEditingEnd] = useState(false);
  const [totalMs, setTotalMs] = useState(0);
  const [fileObjectUrl, setFileObjectUrl] = useState<string | null>(null);
  const [isDurationLoading, setIsDurationLoading] = useState(false);
  const [isGenerateButtonLoading, setIsGenerateButtonLoading] = useState(false);

  // NEW: whether to notify user by email when job is done
  const [notifyByEmail, setNotifyByEmail] = useState<boolean>(false);
  const [agreeToS, setAgreeToS] = useState(false);

  const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);

  const outputFormatOptions = useMemo(
    () => [
      { label: "MP3", value: 0 },
      { label: "MP4", value: 1 },
      { label: "WAV", value: 2 },
      { label: "AAC", value: 3 },
      { label: "MKV", value: 4 },
      { label: "WEBM", value: 5 },
      { label: "AVI", value: 6 },
      { label: "MOV", value: 7 },
      { label: "M4A", value: 8 }
    ],
    []
  );

  const formRef = useRef<HTMLFormElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isDragging, setIsDragging] = useState(false);

  const clipLengthMs = Math.max(endMs - startMs, 0);

  // ---------- cost & quota calculations ----------

  // cost in minutes (rounded to 2 decimals, 1s ≈ 0.02)
  const clipCostMinutes = useMemo(() => {
    const minutes = clipLengthMs / 60000; // ms -> minutes
    return Math.round(minutes * 100) / 100; // 2 decimals
  }, [clipLengthMs]);

  const quotaInfo = useMemo(() => {
    if (
      typeof usedProcessingMinutes !== "number" ||
      typeof maxProcessingMinutes !== "number" ||
      maxProcessingMinutes <= 0
    ) {
      return null;
    }

    const used = usedProcessingMinutes;
    const max = maxProcessingMinutes;
    const afterJob = used + clipCostMinutes;
    const exceedingRaw = afterJob - max;
    const exceeding = exceedingRaw > 0 ? Math.round(exceedingRaw * 100) / 100 : 0;
    const remainingNow = max - used;
    const remainingNowRounded = Math.round(remainingNow * 100) / 100;
    const remainingAfter =
      exceeding > 0 ? 0 : Math.round((max - afterJob) * 100) / 100;

    return {
      used,
      max,
      afterJob,
      exceeding,
      remainingNow: remainingNowRounded,
      remainingAfter,
    };
  }, [usedProcessingMinutes, maxProcessingMinutes, clipCostMinutes]);

  // ---------- callbacks ----------

  const setModeUpload = useCallback(() => setMode("upload"), []);
  const setModeUrl = useCallback(() => setMode("url"), []);

  const handleFiles = useCallback((files: FileList | null) => {
    const f = files?.[0];
    if (f) {
      setFile(f);
      const url = URL.createObjectURL(f);
      setFileObjectUrl(url);
      setIsDurationLoading(true);
    }
  }, []);

  const onLoadedMetadata = useCallback(() => {
    if (!mediaRef.current) return;

    const d = mediaRef.current.duration;

    if (!isFinite(d) || d <= 0) {
      setIsDurationLoading(false);
      toast.error('Could not load metadata for file. Either the URL is wrong or it is not supported!');
      return;
    } else {
      const durationMs = mediaRef.current.duration * 1000;
      setTotalMs(durationMs);
      setEndMs(durationMs);
    }

    setIsDurationLoading(false);
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
    },
    [handleFiles]
  );

  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleVideoUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setVideoUrl(e.target.value);
    },
    []
  );

  const handleStartInputChange = useCallback((val: string) => {
    setIsEditingStart(true);
    setStartInput(val);
  }, []);

  const commitStartInput = useCallback(() => {
    setIsEditingStart(false);
    const ms = parseTime(startInput);
    const clamped = ms > endMs ? endMs : ms;
    setStartMs(clamped);
    setStartInput(formatTime(clamped));
  }, [startInput, endMs]);

  const handleEndInputChange = useCallback((val: string) => {
    setIsEditingEnd(true);
    setEndInput(val);
  }, []);

  const commitEndInput = useCallback(() => {
    setIsEditingEnd(false);
    const ms = parseTime(endInput);
    const clamped = ms < startMs ? startMs : ms;
    setEndMs(clamped);
    setEndInput(formatTime(clamped));
  }, [endInput, startMs]);

  const handleStartSlider = useCallback(
    (val: string) => {
      const v = Number(val);
      setStartMs(v > endMs ? endMs : v);
    },
    [endMs]
  );

  const handleEndSlider = useCallback(
    (val: string) => {
      const v = Number(val);
      setEndMs(v < startMs ? startMs : v);
    },
    [startMs]
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback(() => setIsDragging(false), []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") openFileDialog();
    },
    [openFileDialog]
  );

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setIsGenerateButtonLoading(true);

      if ((!videoUrl && !file) || quotaInfo?.exceeding) {
        setIsGenerateButtonLoading(false);
        return;
      }

      if (!agreeToS) {
        toast.error("You must agree with ToS before submitting a job.");
        return;
      }

      const duration = {
        start: startMs,
        end: endMs,
      };

      const action =
        mode === "url"
          ? FileService.downloadFromUrl(
              videoUrl,
              duration,
              outputFormat,
              notifyByEmail,
              outputFileName
            )
          : FileService.downloadFromUpload(
              file!,
              duration,
              outputFormat,
              notifyByEmail,
              outputFileName
            );

      const response = await action;
      if (response.error) {
        toast.error(response.error.message);
        setIsGenerateButtonLoading(false);
        return;
      }

      const jobId = response.data?.jobId!;
      watchJob(
        jobId,
        { onSubscribePushFirst: true },
        (update) =>
        {}
      );

      setIsGenerateButtonLoading(false);
      navigate(AppRoutes.JOBS);
    },
    [
      videoUrl,
      file,
      startMs,
      endMs,
      outputFormat,
      quotaInfo?.exceeding,
      watchJob,
      navigate,
      mode,
      notifyByEmail,
      outputFileName,
      agreeToS
    ]
  );

  const fetchMetadata = useCallback(async (url: string) => {
    setIsDurationLoading(true);
    const response = await FileService.getMetadata(url);
    if (response?.error) {
      toast.error(response.error.message);
      setIsDurationLoading(false);
      return;
    }
    setStartMs(response.data?.duration.start ?? 0);
    setEndMs(response.data?.duration.end ?? 0);
    setTotalMs(response.data?.duration.end ?? 0);
    setIsDurationLoading(false);
  }, []);

  useEffect(() => {
    if (!videoUrl || !/^https?:\/\/\S+/i.test(videoUrl)) return;

    // debounce 400ms to avoid request-per-keystroke
    const t = setTimeout(() => {
      fetchMetadata(videoUrl).catch((err) => {
        if (err?.name !== "AbortError") {
          
        }
      });
    }, 400);

    return () => {
      clearTimeout(t);
    };
  }, [videoUrl, fetchMetadata]);

  const isAudio = file?.type.startsWith("audio/");
  const isVideo = file?.type.startsWith("video/");
  const isValidSource = mode === "upload" ? !!file : videoUrl.trim().length > 0;

  return (
    <section className="clipper-wrapper" id="video-clipper">
      <div className="clipper-card">
        {/* Header */}
        <header className="clipper-header">
          <p className="clipper-subtitle">
            Select a source, choose a time range, and generate a trimmed clip.
          </p>
        </header>

        {/* Mode toggle */}
        <div className="clipper-mode-toggle" role="tablist">
          <Button
            type="button"
            role="tab"
            aria-selected={mode === "upload"}
            className={"clipper-mode-btn" + (mode === "upload" ? " active" : "")}
            onClick={setModeUpload}
          >
            Upload file
          </Button>
          <Button
            type="button"
            role="tab"
            aria-selected={mode === "url"}
            className={"clipper-mode-btn" + (mode === "url" ? " active" : "")}
            onClick={setModeUrl}
          >
            Paste link/URL
          </Button>
        </div>

        {/* Source block */}
        {mode === "upload" ? (
          <div className="clipper-source-block">
            <label className="clipper-label">Video file</label>

            <div
              className={"clipper-dropzone" + (isDragging ? " dragging" : "")}
              role="button"
              tabIndex={0}
              onClick={openFileDialog}
              onKeyDown={onKeyDown}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <div className="dropzone-text">
                <div className="dropzone-title">
                  {file ? "File selected" : "Drag & drop a file here"}
                </div>
                <div className="dropzone-sub">
                  or
                  <Button
                    type="button"
                    className="clipper-choose-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openFileDialog();
                    }}
                  >
                    Choose a file
                  </Button>
                </div>
                <div className="clipper-file-status" aria-live="polite">
                  {file ? (
                    <>
                      Selected:{" "}
                      <span className="clipper-file-name">{file.name}</span>
                    </>
                  ) : (
                    <span className="clipper-file-hint">MP4 / WebM / MOV …</span>
                  )}
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                className="clipper-native-file-input"
              />
            </div>
          </div>
        ) : (
          <div className="clipper-source-block">
            <label className="clipper-label" htmlFor="clipper-url-input">
              Source URL
            </label>
            <input
              id="clipper-url-input"
              type="text"
              placeholder="https://www.mymedia.com/..."
              value={videoUrl}
              onChange={handleVideoUrlChange}
              className="clipper-url-input clipper-input"
            />
            <p className="clipper-hint">
              We'll use this URL only to fetch the clip range. We don't stream
              the whole thing for preview.
            </p>
          </div>
        )}

        {/* Trim controls */}
        <div className="clipper-trim-box-wrapper">
          <div className="clipper-trim-box">
            <div className="clipper-trim-row">
              <div className="trim-field">
                <label className="trim-label" htmlFor="trim-start">
                  Start
                </label>
                <input
                  id="trim-start"
                  type="text"
                  className="trim-input clipper-input"
                  value={isEditingStart ? startInput : formatTime(startMs)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleStartInputChange(e.target.value)
                  }
                  onBlur={commitStartInput}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitStartInput();
                    }
                  }}
                />
              </div>

              <div className="trim-field">
                <label className="trim-label" htmlFor="trim-end">
                  End
                </label>
                <input
                  id="trim-end"
                  type="text"
                  className="trim-input clipper-input"
                  value={isEditingEnd ? endInput : formatTime(endMs)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleEndInputChange(e.target.value)
                  }
                  onBlur={commitEndInput}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitEndInput();
                    }
                  }}
                />
              </div>

              <div className="trim-field">
                <label className="trim-label">Clip length</label>
                <div className="trim-length">{formatTime(clipLengthMs)}</div>
              </div>
            </div>

            <div className="trim-sliders">
              <div className="trim-slider-block">
                <label className="trim-slider-label">Start position</label>
                <input
                  type="range"
                  min={0}
                  max={totalMs}
                  step={50}
                  value={startMs}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleStartSlider(e.target.value)
                  }
                  className="trim-slider"
                />
              </div>

              <div className="trim-slider-block">
                <label className="trim-slider-label">End position</label>
                <input
                  type="range"
                  min={0}
                  max={totalMs}
                  step={50}
                  value={endMs}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleEndSlider(e.target.value)
                  }
                  className="trim-slider"
                />
              </div>
            </div>
          </div>

          {isDurationLoading && (
            <div
              className="clipper-trim-overlay"
              aria-busy="true"
              aria-label="Loading video duration"
            >
              <div className="clipper-spinner" />
            </div>
          )}
        </div>

        {/* Output format + file name + cost + Notify + Submit */}
        <form ref={formRef} onSubmit={onSubmit} className="clipper-submit">
          {/* filename + format aligned responsively */}
          <div className="clipper-submit-top">
            {/* NEW: output file name */}
            <div className="clipper-filename-block">
              <label
                className="clipper-label"
                htmlFor="clipper-filename-input"
              >
                Output file name
              </label>
              <input
                id="clipper-filename-input"
                type="text"
                className="clipper-input clipper-filename-input"
                placeholder="My awesome clip"
                value={outputFileName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setOutputFileName(e.target.value)
                }
              />
              <p className="clipper-hint">
                This will be used as the base name of your downloaded file. The
                extension is added automatically.
              </p>
            </div>

            <div className="clipper-output-block">
              <label
                className="clipper-label"
                htmlFor="clipper-output-format"
              >
                Output format
              </label>
              <div className="clipper-select-wrapper">
                <select
                  id="clipper-output-format"
                  className="clipper-select clipper-input"
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(Number(e.target.value))}
                >
                  {outputFormatOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Cost / quota hint */}
          {quotaInfo && (
            <p
              className={
                "clipper-cost-hint" +
                (quotaInfo.exceeding > 0 ? " clipper-cost-hint--error" : "")
              }
            >
              {quotaInfo.exceeding > 0 ? (
                <>
                  This clip will cost{" "}
                  <strong>{clipCostMinutes.toFixed(2)} minutes</strong> and
                  would exceed your monthly processing limit by{" "}
                  <strong>{quotaInfo.exceeding.toFixed(2)} minutes</strong>.
                  Reduce the clip length or upgrade your plan.
                </>
              ) : (
                <>
                  This clip will cost{" "}
                  <strong>{clipCostMinutes.toFixed(2)} minutes</strong> of your
                  monthly processing time. You’ll have{" "}
                  <strong>{quotaInfo.remainingAfter.toFixed(2)} minutes</strong>{" "}
                  left after this job.
                </>
              )}
            </p>
          )}

          {/* NEW: Notify-by-email toggle */}
          <div className="clipper-notify-row">
            <label className="clipper-notify-label">
              <input
                type="checkbox"
                className="clipper-notify-checkbox"
                checked={notifyByEmail}
                onChange={(e) => setNotifyByEmail(e.target.checked)}
              />
              <span className="clipper-notify-text">
                <span className="clipper-notify-title">
                  Email me when this clip is ready
                </span>
                <span className="clipper-notify-sub">
                  We’ll send a one-time notification to your account email when
                  the job finishes.
                </span>
              </span>
            </label>
          </div>
          <div className="clipper-notify-row">
            <label className="clipper-notify-label">
              <input
                type="checkbox"
                className="clipper-notify-checkbox"
                required={true}
                checked={agreeToS}
                onChange={(e) => setAgreeToS(e.target.checked)}
              />
              <span className="clipper-notify-text">
                <span className="clipper-notify-title">
                  I confirm I own this content or have permission to use it, and I agree to the Terms of Service and Privacy Policy.
                </span>
              </span>
            </label>
          </div>

          <Button
            type="submit"
            loading={isGenerateButtonLoading}
            disabled={!isValidSource}
            className="clipper-submit-btn"
          >
            Generate Clip
          </Button>

          <p className="clipper-legal-hint">
            We'll only cut the selected range. Please make sure you have rights
            to process / edit this video.
          </p>
        </form>
      </div>

      {fileObjectUrl && (isAudio || isVideo) && (
        <>
          {isAudio ? (
            <audio
              ref={mediaRef as React.RefObject<HTMLAudioElement>}
              src={fileObjectUrl}
              preload="metadata"
              style={{ display: "none" }}
              onLoadedMetadata={onLoadedMetadata}
            />
          ) : (
            <video
              ref={mediaRef as React.RefObject<HTMLVideoElement>}
              src={fileObjectUrl}
              preload="metadata"
              style={{ display: "none" }}
              onLoadedMetadata={onLoadedMetadata}
            />
          )}
        </>
      )}
    </section>
  );
};

export default VideoClipper;
